import { useState, useEffect } from 'react'
import { 
  CheckIcon, 
  XMarkIcon, 
  EyeIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  StarIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import { StarIcon as SolidStarIcon } from '@heroicons/react/24/solid'
import UserAvatar from '../../components/UserAvatar'

interface Dispute {
  id: string
  student: {
    firstName: string
    lastName: string
    email: string
    avatar?: string
  }
  teacher: {
    id: string
    title: string
    user: {
      firstName: string
      lastName: string
      email: string
      avatar?: string
    }
  }
  course: {
    title: string
    subject: string
  }
  rating: number
  review?: string
  disputeReason?: string
  disputeStatus?: 'pending' | 'approved' | 'rejected'
  disputeSubmittedAt?: string
  disputeReviewedAt?: string
  disputeReviewNotes?: string
  ratingExcluded: boolean
  date: string
  startTime: string
  endTime: string
  totalPrice: number
}

const AdminRatingDisputes = () => {
  const [disputes, setDisputes] = useState<Dispute[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedDispute, setSelectedDispute] = useState<Dispute | null>(null)
  const [showReviewModal, setShowReviewModal] = useState(false)
  const [reviewNotes, setReviewNotes] = useState('')
  const [reviewDecision, setReviewDecision] = useState<'approved' | 'rejected'>('approved')
  const [activeTab, setActiveTab] = useState<'pending' | 'all'>('pending')
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  })

  useEffect(() => {
    loadDisputes()
  }, [activeTab, pagination.page])

  const loadDisputes = async () => {
    try {
      setLoading(true)
      
      let endpoint = '/api/rating-disputes/pending'
      if (activeTab === 'all') {
        endpoint = `/api/rating-disputes/all?status=all&page=${pagination.page}&limit=${pagination.limit}`
      }

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        
        if (activeTab === 'pending') {
          setDisputes(data)
        } else {
          setDisputes(data.disputes)
          setPagination(prev => ({
            ...prev,
            total: data.pagination.total,
            pages: data.pagination.pages
          }))
        }
      }
    } catch (error) {
      console.error('加载异议数据失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleReviewDispute = async () => {
    if (!selectedDispute || !reviewNotes.trim()) {
      alert('请填写审核意见')
      return
    }

    try {
      const response = await fetch(`/api/rating-disputes/${selectedDispute.id}/review`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          decision: reviewDecision,
          reviewNotes
        })
      })

      if (response.ok) {
        alert(`异议${reviewDecision === 'approved' ? '通过' : '拒绝'}成功`)
        setShowReviewModal(false)
        setSelectedDispute(null)
        setReviewNotes('')
        loadDisputes()
      } else {
        const error = await response.json()
        alert(`审核失败: ${error.message}`)
      }
    } catch (error) {
      console.error('审核异议失败:', error)
      alert('审核失败，请重试')
    }
  }

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <div key={star} className="relative">
            {star <= rating ? (
              <SolidStarIcon className="h-4 w-4 text-yellow-400" />
            ) : (
              <StarIcon className="h-4 w-4 text-gray-300" />
            )}
          </div>
        ))}
        <span className="ml-1 text-xs text-gray-600">({rating}/5)</span>
      </div>
    )
  }

  const getDisputeStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'approved': return 'bg-green-100 text-green-800'
      case 'rejected': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getDisputeStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return '待审核'
      case 'approved': return '已通过'
      case 'rejected': return '已拒绝'
      default: return '未知'
    }
  }

  const getDisputeStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <ClockIcon className="h-4 w-4" />
      case 'approved': return <CheckCircleIcon className="h-4 w-4" />
      case 'rejected': return <XCircleIcon className="h-4 w-4" />
      default: return null
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 页面标题 */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">评分异议管理</h1>
        <p className="mt-2 text-sm text-gray-700">审核教师提交的评分异议</p>
      </div>

      {/* 选项卡 */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => {
              setActiveTab('pending')
              setPagination(prev => ({ ...prev, page: 1 }))
            }}
            className={`${
              activeTab === 'pending'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
          >
            待审核异议
            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              {activeTab === 'pending' ? disputes.length : '...'}
            </span>
          </button>
          <button
            onClick={() => {
              setActiveTab('all')
              setPagination(prev => ({ ...prev, page: 1 }))
            }}
            className={`${
              activeTab === 'all'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
          >
            全部异议
            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-900">
              {pagination.total}
            </span>
          </button>
        </nav>
      </div>

      {/* 异议列表 */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {disputes.length > 0 ? (
          <ul className="divide-y divide-gray-200">
            {disputes.map((dispute) => (
              <li key={dispute.id}>
                <div className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 flex-1 min-w-0">
                      <UserAvatar user={dispute.teacher.user} size="sm" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-2">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            教师: {dispute.teacher.user.firstName} {dispute.teacher.user.lastName}
                          </p>
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            {dispute.teacher.title}
                          </span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getDisputeStatusColor(dispute.disputeStatus || '')}`}>
                            {getDisputeStatusIcon(dispute.disputeStatus || '')}
                            <span className="ml-1">{getDisputeStatusLabel(dispute.disputeStatus || '')}</span>
                          </span>
                          {dispute.ratingExcluded && (
                            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                              已排除计算
                            </span>
                          )}
                        </div>
                        
                        <div className="mb-2">
                          <p className="text-sm text-gray-900">
                            <strong>课程:</strong> {dispute.course.title} ({dispute.course.subject})
                          </p>
                          <p className="text-sm text-gray-500">
                            <strong>学生:</strong> {dispute.student.firstName} {dispute.student.lastName}
                          </p>
                          <div className="flex items-center mt-1">
                            <strong className="text-sm text-gray-500 mr-2">评分:</strong>
                            {renderStars(dispute.rating)}
                          </div>
                        </div>

                        <div className="space-y-1">
                          <p className="text-sm text-gray-600">
                            <strong>学生评价:</strong> "{dispute.review}"
                          </p>
                          <p className="text-sm text-gray-600">
                            <strong>异议原因:</strong> {dispute.disputeReason}
                          </p>
                          <p className="text-sm text-gray-500">
                            提交时间: {new Date(dispute.disputeSubmittedAt || '').toLocaleString('zh-CN')}
                          </p>
                          {dispute.disputeReviewNotes && (
                            <p className="text-sm text-gray-600">
                              <strong>审核意见:</strong> {dispute.disputeReviewNotes}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {dispute.disputeStatus === 'pending' && (
                        <>
                          <button
                            onClick={() => {
                              setSelectedDispute(dispute)
                              setReviewDecision('approved')
                              setShowReviewModal(true)
                            }}
                            className="inline-flex items-center px-3 py-1 border border-green-300 shadow-sm text-sm leading-4 font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                          >
                            <CheckIcon className="h-4 w-4 mr-1" />
                            通过
                          </button>
                          <button
                            onClick={() => {
                              setSelectedDispute(dispute)
                              setReviewDecision('rejected')
                              setShowReviewModal(true)
                            }}
                            className="inline-flex items-center px-3 py-1 border border-red-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            <XMarkIcon className="h-4 w-4 mr-1" />
                            拒绝
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-center py-12">
            <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {activeTab === 'pending' ? '暂无待审核异议' : '暂无异议记录'}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {activeTab === 'pending' ? '所有异议已处理完毕' : '还没有教师提交评分异议'}
            </p>
          </div>
        )}
      </div>

      {/* 分页 */}
      {activeTab === 'all' && pagination.pages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
              disabled={pagination.page === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              上一页
            </button>
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.pages, prev.page + 1) }))}
              disabled={pagination.page === pagination.pages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              下一页
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                显示第 <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> 到{' '}
                <span className="font-medium">
                  {Math.min(pagination.page * pagination.limit, pagination.total)}
                </span>{' '}
                条，共 <span className="font-medium">{pagination.total}</span> 条记录
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                  disabled={pagination.page === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  上一页
                </button>
                {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                  let pageNumber;
                  if (pagination.pages <= 5) {
                    pageNumber = i + 1;
                  } else if (pagination.page <= 3) {
                    pageNumber = i + 1;
                  } else if (pagination.page >= pagination.pages - 2) {
                    pageNumber = pagination.pages - 4 + i;
                  } else {
                    pageNumber = pagination.page - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => setPagination(prev => ({ ...prev, page: pageNumber }))}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        pagination.page === pageNumber
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.pages, prev.page + 1) }))}
                  disabled={pagination.page === pagination.pages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  下一页
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* 审核异议模态框 */}
      {showReviewModal && selectedDispute && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-lg shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {reviewDecision === 'approved' ? '通过异议' : '拒绝异议'}
              </h3>
              <button
                onClick={() => setShowReviewModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">关闭</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-4">
              <div className="bg-gray-50 p-3 rounded-md mb-4">
                <p className="text-sm text-gray-600">
                  <strong>教师:</strong> {selectedDispute.teacher.user.firstName} {selectedDispute.teacher.user.lastName}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>课程:</strong> {selectedDispute.course.title}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>学生:</strong> {selectedDispute.student.firstName} {selectedDispute.student.lastName}
                </p>
                <div className="flex items-center mt-1">
                  <strong className="text-sm text-gray-600 mr-2">评分:</strong>
                  {renderStars(selectedDispute.rating)}
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  <strong>学生评价:</strong> "{selectedDispute.review}"
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  <strong>异议原因:</strong> {selectedDispute.disputeReason}
                </p>
              </div>

              <div className={`p-3 rounded-md mb-4 ${reviewDecision === 'approved' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <p className={`text-sm font-medium ${reviewDecision === 'approved' ? 'text-green-800' : 'text-red-800'}`}>
                  {reviewDecision === 'approved' 
                    ? '⚠️ 通过此异议将从评分计算中排除该评价' 
                    : '❌ 拒绝此异议，该评分将继续计入总评分'}
                </p>
              </div>

              <label className="block text-sm font-medium text-gray-700 mb-2">
                审核意见 <span className="text-red-500">*</span>
              </label>
              <textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                rows={4}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder={reviewDecision === 'approved' 
                  ? "请说明通过异议的理由..." 
                  : "请说明拒绝异议的理由..."}
                required
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowReviewModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                取消
              </button>
              <button
                onClick={handleReviewDispute}
                disabled={!reviewNotes.trim()}
                className={`flex-1 px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                  reviewDecision === 'approved'
                    ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                    : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                }`}
              >
                确认{reviewDecision === 'approved' ? '通过' : '拒绝'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminRatingDisputes 